import { dedupeConcurrentRequest } from "@/app/_utils/dedupeConcurrentRequest";
import { getCsrfToken } from "@/app/_utils/csrf";

export type Location = {
  id: string;
  name: string;
  address_line_1: string;
  address_line_2?: string | null;
  city: string;
  state: string;
  zip: string;
  latitude?: number | null;
  longitude?: number | null;
  formatted_address?: string | null;
  created_at: string;
  updated_at: string;
  show_count?: number;
  last_show_date?: string | null;
  last_show_id?: string | null;
  last_show_img_url?: string | null;
  last_show_name?: string | null;
  total_revenue?: number;
};

export type LocationKpis = {
  revenue: number;
  num_shows: number;
  avg_capacity: number;
  tickets_available: number;
  tickets_sold: number;
};

export type LocationSalesPoint = {
  date: string;
  cumulative_revenue: number;
};

export type LocationSalesSummary = {
  sell_through_pct: number;
  tickets_sold: number;
  tickets_available: number;
  gross_revenue: number;
  avg_ticket_price: number;
  refund_count: number;
  refund_amount: number;
  series: LocationSalesPoint[];
};

export type LocationPerformerSummary = {
  performer_id: string;
  first_name: string;
  last_name: string;
  img_url?: string | null;
  times: number;
};

export type LocationShowRow = {
  id: string;
  title: string;
  variant_name?: string | null;
  start: string;
  img_url?: string | null;
  capacity: number;
  sold: number;
  revenue: number;
  ticket_price: number;
  status: string;
};

export type LocationDetail = {
  location: Location;
  kpis: LocationKpis;
  sales: LocationSalesSummary;
  performers: LocationPerformerSummary[];
  shows: LocationShowRow[];
};

export type AddressValidationResult = {
  latitude: number | null;
  longitude: number | null;
  formatted_address: string | null;
  existing_location: { id: string; name: string } | null;
};

class LocationService {
  static instance: LocationService;
  csrfToken = getCsrfToken();
  baseUrl = `${import.meta.env.VITE_API_BASE_URL}`;
  private readonly inflight = new Map<string, Promise<unknown>>();

  getLocationAsync = async (id: string): Promise<Location> => {
    const res = await fetch(`${this.baseUrl}show/location/${id}/`, {
      headers: { "Content-Type": "application/json" },
      credentials: "include",
    });
    return await res.json();
  };

  getLocationsAsync = async (): Promise<Location[]> => {
    return dedupeConcurrentRequest(this.inflight, "list", async () => {
      const res = await fetch(`${this.baseUrl}show/location/`, {
        headers: { "Content-Type": "application/json" },
        credentials: "include",
      });
      return await res.json();
    }) as Promise<Location[]>;
  };

  getLocationDetailAsync = async (id: string): Promise<LocationDetail> => {
    const res = await fetch(`${this.baseUrl}show/location/${id}/detail/`, {
      headers: { "Content-Type": "application/json" },
      credentials: "include",
    });
    return await res.json();
  };

  validateAddressAsync = async (fields: {
    address_line_1?: string | null;
    address_line_2?: string | null;
    city?: string | null;
    state?: string | null;
    zip?: string | null;
    exclude_id?: string;
  }): Promise<AddressValidationResult> => {
    const res = await fetch(`${this.baseUrl}show/location/validate-address/`, {
      method: "POST",
      body: JSON.stringify(fields),
      headers: {
        "Content-Type": "application/json",
        "X-CSRFToken": this.csrfToken,
      },
      credentials: "include",
    });
    return await res.json();
  };

  createLocationAsync = async (location: Partial<Location>): Promise<Location> => {
    const res = await fetch(`${this.baseUrl}show/location/`, {
      method: "POST",
      body: JSON.stringify(location),
      headers: {
        "Content-Type": "application/json",
        "X-CSRFToken": this.csrfToken,
      },
      credentials: "include",
    });

    return await res.json();
  };

  updateLocationAsync = async (id: string, location: Partial<Location>): Promise<Location> => {
    const res = await fetch(`${this.baseUrl}show/location/${id}/`, {
      method: "PUT",
      body: JSON.stringify(location),
      headers: {
        "Content-Type": "application/json",
        "X-CSRFToken": this.csrfToken,
      },
      credentials: "include",
    });

    return await res.json();
  };

  deleteLocationAsync = async (id: string): Promise<Response> => {
    return await fetch(`${this.baseUrl}show/location/${id}/`, {
      method: "DELETE",
      headers: { "X-CSRFToken": this.csrfToken },
      credentials: "include",
    });
  };
}

export default new LocationService();
